import { ProductRecommendationArea } from 'components/Blocks/ProductRecommendationArea/ProductRecommendationArea';
import { ProductRecommendationAreasSkeleton } from 'components/Blocks/ProductRecommendationArea/ProductRecommendationAreasSkeleton';
import { useRefetchCart } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useDisplayedProductRecommendationAreas } from 'hooks/productRecommendationArea/useProductRecommendationAreas';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useRouter } from 'next/router';
import { ProductRecommendationAreaType } from 'types/productRecommendationArea';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type ProductRecommendationAreasProps = {
    productRecommendationAreas: ProductRecommendationAreaType[];
    fallbackGtmId: string;
    maxVisibleItems?: number;
    prepareProductOrderOnMount?: boolean;
    productSliderClassName?: string;
};

export const ProductRecommendationAreas: FC<ProductRecommendationAreasProps> = ({
    productRecommendationAreas,
    fallbackGtmId,
    maxVisibleItems,
    prepareProductOrderOnMount = false,
    productSliderClassName,
}) => {
    const router = useRouter();
    const { url: domainConfigUrl } = useDomainConfig();
    const [cartUrl] = getInternationalizedStaticUrls(['/cart'], domainConfigUrl);
    const refetchCart = useRefetchCart();
    const { productRecommendationAreasForDisplay, isProductOrderPending } = useDisplayedProductRecommendationAreas(
        productRecommendationAreas,
        prepareProductOrderOnMount,
    );

    const handleAddProductFromRecommendationArea = () => {
        if (router.asPath === cartUrl) {
            dispatchBroadcastChannel({ name: 'refetchCart' });
            refetchCart();
        }
    };

    if (isProductOrderPending) {
        return (
            <ProductRecommendationAreasSkeleton
                maxVisibleItems={maxVisibleItems}
                productRecommendationAreas={productRecommendationAreas}
                productSliderClassName={productSliderClassName}
            />
        );
    }

    if (productRecommendationAreasForDisplay.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-y-4 pt-2">
            {productRecommendationAreasForDisplay.map((productRecommendationArea, index) => (
                <ProductRecommendationArea
                    key={index}
                    fallbackGtmId={fallbackGtmId}
                    maxVisibleItems={maxVisibleItems}
                    productRecommendationArea={productRecommendationArea}
                    productSliderClassName={productSliderClassName}
                    onAddToCart={handleAddProductFromRecommendationArea}
                />
            ))}
        </div>
    );
};
